<script setup lang="ts">
import { computed } from 'vue'
import { TOKENS, isDarkTone, type Tone } from '../tokens'
import { statusGroup, statusLabel, type StatusGroup } from '../status'
import { toneVars } from '../tone-vars'

/**
 * 状态标签。**全仓唯一一份**状态中文名与配色分组,两端都从这里取。
 *
 * 标签表在 `../status.ts`,不在这个文件里 —— 视图里的筛选下拉也要用同一份
 * 中文名,塞在组件里就得再抄一遍,而两份抄本的第一次分叉没人会发现。
 *
 * 未知状态:原样显示字符串 + 灰色。这不是「优雅降级」,是**让它刺眼** ——
 * 服务端加了新状态而这里没跟上时,页面上会出现一个灰底的英文串,
 * 一眼就能看出是漏了一处映射(见 status.ts 的注释)。
 *
 * ═══ label / group 这两个覆盖参数为什么在这里,而不是再开一个组件 ═══
 *
 * 承运商申请与计价表版本也有状态,它们的中文名与配色分组都在 labels.ts 里。
 * 若为它们再写一个 pill,那个组件会与这个长得越来越不一样(今天差 1px 内边距,
 * 三个月后差一个圆角),而**页面上不会有人发现** —— 两个 pill 不在同一屏里。
 * 覆盖参数让「同一个形状」这件事由组件自己保证:视图只提供文案与分组。
 *
 * 两个参数都不传时行为一字未变(订单状态表),所以监管端与订单页一行都不用改。
 */
const props = withDefaults(
  defineProps<{
    status: string
    tone?: Tone
    size?: 'sm' | 'md'
    /** 覆盖显示文案。不传走订单状态表;判不出取值时原样显示 status(与 statusLabel 同) */
    label?: string
    /** 覆盖颜色分组。不传走订单状态表;判不出时按 draft(灰)渲染(与 statusGroup 同) */
    group?: StatusGroup
  }>(),
  { tone: 'light', size: 'md' },
)

const text = computed(() => props.label ?? statusLabel(props.status))

const vars = computed(() => {
  const palette = isDarkTone(props.tone) ? TOKENS.semantic.tag.dark : TOKENS.semantic.tag.light
  const color = palette[props.group ?? statusGroup(props.status)]
  return {
    ...toneVars(props.tone),
    '--sd-tag-text': color.text,
    '--sd-tag-bg': color.bg,
    '--sd-tag-border': color.border,
  }
})
</script>

<template>
  <span class="tag" :class="`size-${props.size}`" :style="vars">{{ text }}</span>
</template>

<style scoped>
.tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0 10px;
  border-radius: var(--sd-radius-pill);
  font-size: 12px;
  line-height: 22px;
  white-space: nowrap;
  color: var(--sd-tag-text);
  background: var(--sd-tag-bg);
  border: 1px solid var(--sd-tag-border);
}

.size-md {
  line-height: 24px;
  padding: 0 11px;
}

.size-sm {
  font-size: 11px;
  line-height: 20px;
  padding: 0 8px;
}
</style>
